import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, HStack, Icon, Text, Textarea, VStack } from '@chakra-ui/react';
import { LuListPlus } from 'react-icons/lu';
import {
	COMPOSER_ADVISORY_EXIT_MS,
	DURATION_FORM_REVEAL_CLOSE_MS,
	DURATION_FORM_REVEAL_OPEN_MS,
	EASE_OUT,
	getSlideInCss,
} from '@/components/ui/motion.ts';
import { BOTTOM_BAR_SHADOW } from '@/components/ui/shadows.ts';
import { dropdownScrollbarCss } from '@/components/ui/scrollbar.ts';
import { COMPOSER_ACTION_LABEL_PILL_PROPS } from './composerActionButtonStyles.ts';
import { useAssistantComposer } from './useAssistantComposer.ts';
import { useCloseAddOnRouteChange } from './useCloseAddOnRouteChange.ts';
import { useRecordComposer } from './useRecordComposer.ts';
import { TaskComposerController, type TaskComposerParent, useTaskComposer } from './useTaskComposer.ts';
import { useComposerAddRegionLayout } from './useComposerAddRegionLayout.ts';
import ComposerAddButton from './ComposerAddButton.tsx';
import ComposerTabStrip from './ComposerTabStrip.tsx';
import ComposerForms from './ComposerForms.tsx';
import { composerSubmitKeyDown } from './composerSubmitShortcut.ts';
import ComposerAttachActions from './ComposerAttachActions.tsx';
import ComposerPrivacyToggle from './ComposerPrivacyToggle.tsx';
import ComposerTaskScheduleControls from './ComposerTaskScheduleControls.tsx';
import ComposerChildTasks from './ComposerChildTasks.tsx';
import ComposerSubmitButton from './ComposerSubmitButton.tsx';
import ComposerAddSheet from './ComposerAddSheet.tsx';
import ComposerFocusBackdrop from './ComposerFocusBackdrop.tsx';
import { useComposerDockFocus } from './composerDockFocus.ts';
import AskReadinessAdvisory from './AskReadinessAdvisory.tsx';
import FileDropOverlay from './FileDropOverlay.tsx';
import { useWindowFileDrag } from '@/hooks/ui/useWindowFileDrag.ts';
import { useAutoResizeTextarea } from '@/hooks/ui/useAutoResizeTextarea.ts';
import { useClickOutside } from '@/hooks/useClickOutside.ts';
import { useIsMobileViewport } from '@/hooks/ui/useIsMobileViewport.ts';
import { isAsking } from '@/components/common/search/isAsking.ts';
import {
	COMPOSER_ACTION_ROW_SLIDE_MS,
	COMPOSER_ADD_BUTTON_OVERHANG,
	COMPOSER_ADD_FADE_LEAD_MS,
	COMPOSER_ACTION_ROW_CONTAINER_CSS,
	COMPOSER_ATTACH_SLIDE_PX,
	COMPOSER_COLLAPSED_RADIUS,
	COMPOSER_CHILD_REMOVE_MS,
	COMPOSER_EXPANDED_RADIUS,
	COMPOSER_FOCUS_SHADOW,
	COMPOSER_MULTILINE_THRESHOLD_PX,
	COMPOSER_PORTAL_CLICK_BOUNDARY_SELECTOR,
	COMPOSER_PRIVACY_SLIDE_PX,
	COMPOSER_SUBMIT_LABEL,
	COMPOSER_SUBMIT_SHORT_LABEL,
	COMPOSER_SUBTASK_LABEL_CSS,
	COMPOSER_SUBTASK_PILL_CSS,
	COMPOSER_TEXTAREA_MAX_H,
	COMPOSER_TABS,
	ComposerTab,
	TRANSITION_COMPOSER_MORPH,
} from './composerConstants.ts';
import {
	getAskReadinessCopy,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { propertyNavigateOptions, PropertyRouteTo } from '@/routes';
import { formatLocalDateForApi } from '@/utils/dateUtils.ts';
import { useHousePermissions } from '@/hooks/useHasPermission.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import type { CustomEntryOut } from '@/client';
import type { ContactAddRequest } from '@/components/services/contact/contactFormValues';
import ProviderContactFields from '@/components/services/contact/ProviderContactFields.tsx';
import { useAddProviderComposer } from '@/components/services/contact/useAddProviderComposer.ts';
import { toaster } from '@/components/ui/toaster.tsx';
import { ZIndex } from '@/utils/zIndex.ts';

export interface AdditionalComposerTab {
	tab: ComposerTab;
	form: React.ReactNode;
	canSubmit: boolean;
	isSubmitting: boolean;
	isDirty: boolean;
	disabledHint: string;
	onSubmit: () => Promise<boolean | void> | boolean | void;
	/** Empties the draft. */
	reset: () => void;
	/** The form has opened out past what a compact surface shows. */
	wantsRoom?: boolean;
}

interface AssistantInputProps {
	query: string;
	setQuery: (query: string) => void;
	onSubmit: (e: React.FormEvent) => Promise<void>;
	isLoadingData: boolean;
	isRunning: boolean;
	autoFocus?: boolean;
	/**
	 * Centered (middle-screen) placement grows the add region DOWN, so the + sits
	 * at the top and stays put. Bottom-docked placements grow UP and keep the +
	 * at the bottom. Defaults to the bottom-docked behaviour.
	 */
	expandsDown?: boolean;
	/** Ask-field placeholder. Defaults to the assistant's "Ask a question…". */
	placeholder?: string;
	mobilePlaceholder?: string;
	/**
	 * Whether the host has somewhere to show the + add affordance. The composer still intersects
	 * this with the current user's record, task, and contact permissions before drawing it or
	 * accepting drops.
	 */
	showAddButton?: boolean;
	/**
	 * When set, the host wants live list-search from the bar (records page). The text is
	 * forwarded as a filter while it reads as a search term, and cleared once it reads as a
	 * question — so the list doesn't filter on a half-typed prompt. Submit still calls onSubmit.
	 */
	onFilterChange?: (filterText: string) => void;
	/**
	 * Wait until the shared search/Ask heuristic recognises question intent before showing the
	 * KB-readiness explanation. Used by mixed-purpose hosts that manage filtering themselves.
	 */
	waitForAskIntent?: boolean;
	/** Optional icon at the field's leading edge (e.g. a search glyph on the records page). */
	leadingIcon?: React.ReactNode;
	/** Tooltip on the submit button when a query is ready. Defaults to "Submit query". */
	askLabel?: string;
	/** KB state used by the display-only book badge and typing-gated Ask advisory. */
	askReadinessState?: KnowledgeBaseReadinessStateValue | null;
	askReadinessContext?: {
		affectedFileCount: number;
		filePublicId?: string;
	};
	/** Optional host-owned dismissal so the advisory survives switching between composer instances. */
	askReadinessDismissed?: boolean;
	onDismissAskReadiness?: () => void;
	/** Which add tab should open first when the + is pressed. Defaults to Note or file. */
	initialAddTab?: ComposerTab;
	/**
	 * Initial privacy for records added via the + composer, so it can follow the host's current
	 * privacy filter (e.g. default private while viewing the Private filter). Defaults to false.
	 */
	defaultPrivate?: boolean;
	/** Resting shadow for hosts that need stronger contrast than the chat surface. */
	surfaceShadow?: string;
	/** Focus/open shadow paired with `surfaceShadow`. */
	focusShadow?: string;
	/** Reports the composer mode so a host (e.g. the heading) can react to it. */
	onHeadingModeChange?: (mode: 'ask' | 'record' | 'task' | 'contact') => void;
	/** Enables the one-level grouped-task composer trial. Relationships remain client-only. */
	taskChildCreationEnabled?: boolean;
	/** Opens the Task composer to append one client-only sub-task to this existing task. */
	taskParent?: TaskComposerParent;
	/** Removes the existing-task context while leaving the Task composer open as a top-level draft. */
	onClearTaskParent?: () => void;
	/** Identifies a newly stored task so a schedule host can animate its eventual materialised card. */
	onTaskSubmitted?: (entry: CustomEntryOut) => void;
	/** A page-owned entry type, such as Contact, that joins the shared add-mode tabs. */
	additionalAddTab?: AdditionalComposerTab;
	/** Opens a tab from another page affordance; changing `key` repeats the request. */
	openAddTabRequest?: { key: number; tab: ComposerTab } | null;
	/** Optionally preselects a trade, or a whole draft, when another page affordance opens Contact. */
	contactAddRequest?: ContactAddRequest | null;
	/** Page-specific follow-up after adding a contact, such as opening its detail pane. */
	onContactAdded?: (contactId: number | null) => void;
}

const getAddDisabledHint = (isRecordTab: boolean, task: TaskComposerController): string => {
	if (isRecordTab) return 'Add a note or upload a file';
	if (!task.title.trim()) return 'Add a task title';
	if (!task.startDate) return 'Pick a due date';
	const incompleteChild = task.children.find((child) => !child.title.trim() || !child.startDate);
	if (!incompleteChild) return 'Complete the task';
	return !incompleteChild.title.trim() ? 'Add each sub-task title' : 'Pick each sub-task due date';
};

const getAskReadinessTooltip = (state: KnowledgeBaseReadinessStateValue, affectedFileCount?: number) => {
	const { advisory } = getAskReadinessCopy(state, affectedFileCount);
	return <Text as="span">{advisory}</Text>;
};

const AssistantInput: React.FC<AssistantInputProps> = (props: AssistantInputProps) => {
	const { house } = useCurrentHouse();
	const navigate = useNavigate();
	const isRecordsPage = useRouterState({ select: (state) => state.location.pathname.endsWith('/records') });
	const {
		query,
		setQuery,
		onSubmit,
		isLoadingData,
		isRunning,
		autoFocus = true,
		expandsDown = false,
		placeholder = 'Ask a question...',
		mobilePlaceholder,
		onFilterChange,
		waitForAskIntent = false,
		showAddButton = true,
		leadingIcon,
		askLabel = 'Submit query',
		askReadinessState = null,
		askReadinessContext,
		askReadinessDismissed,
		onDismissAskReadiness,
		initialAddTab = ComposerTab.Record,
		defaultPrivate = false,
		surfaceShadow = BOTTOM_BAR_SHADOW,
		focusShadow = COMPOSER_FOCUS_SHADOW,
		onHeadingModeChange,
		taskChildCreationEnabled = false,
		taskParent,
		onClearTaskParent,
		onTaskSubmitted,
		additionalAddTab,
		openAddTabRequest,
		contactAddRequest,
		onContactAdded,
	} = props;

	const { canUploadDocuments, canCreateScheduleTask, canManageNetwork } = useHousePermissions();
	const { isSuperAdmin } = useAuth();
	const canCreateRecords = canUploadDocuments || isSuperAdmin;
	const canCreateTasks = canCreateScheduleTask || isSuperAdmin;
	const canCreateContacts = canManageNetwork || isSuperAdmin;
	const handleContactAdded = useCallback(
		(contactId: number | null) => {
			if (onContactAdded) {
				onContactAdded(contactId);
				return;
			}
			toaster.create({ title: 'Contact added', type: 'success' });
		},
		[onContactAdded],
	);
	const contact = useAddProviderComposer({
		houseId: house.id,
		onAdded: handleContactAdded,
	});
	/**
	 * Expanding the composer on the Contact tab also unfolds the contact's details, once. Expanding
	 * asks for room, and the folded fields are the only thing more room would show; after that first
	 * time the fold is the reader's, and expanding again leaves it where they put it.
	 */
	const [contactMoreDetailsRequest, setContactMoreDetailsRequest] = useState(0);
	const hasUnfoldedContactDetailsRef = useRef(false);
	const setSelectedContactCategoryCode = contact.setSelectedCategoryCode;
	const resetContactDraft = contact.reset;
	const startContactFromDraft = contact.controller.startFromDraft;
	/**
	 * A request that names a job opens the form with the job filled in. One that carries a draft
	 * replaces whatever was being typed with it: it was asked for from a saved contact, and a half
	 * finished one mixed into it would file two people as one.
	 */
	const answeredContactRequestKeyRef = useRef<number | null>(null);
	useEffect(() => {
		if (!contactAddRequest || answeredContactRequestKeyRef.current === contactAddRequest.key) return;
		answeredContactRequestKeyRef.current = contactAddRequest.key;
		if (contactAddRequest.draft) resetContactDraft();
		if (contactAddRequest.categoryCode !== undefined)
			setSelectedContactCategoryCode(contactAddRequest.categoryCode);
		if (contactAddRequest.draft) startContactFromDraft(contactAddRequest.draft);
	}, [contactAddRequest, resetContactDraft, setSelectedContactCategoryCode, startContactFromDraft]);
	const contactAddTab: AdditionalComposerTab | undefined =
		showAddButton && canCreateContacts
			? {
					tab: ComposerTab.Contact,
					form: (
						<ProviderContactFields
							key={contact.draftKey}
							controller={contact.controller}
							selectedCategoryCode={contact.selectedCategoryCode}
							onCategoryChange={contact.setSelectedCategoryCode}
							px={4}
							// The action row beneath has 8px of its own padding in the sheet and 16px inline.
							pb={{ base: 4, md: 1 }}
							moreDetailsOpenRequest={contactMoreDetailsRequest}
						/>
					),
					canSubmit: contact.canSubmit,
					isSubmitting: contact.isSubmitting,
					isDirty: contact.isDirty,
					disabledHint: contact.disabledHint,
					// A new contact starts over: the unfold is once per draft, not per page.
					onSubmit: async () => {
						const submitted = await contact.submit();
						if (submitted) hasUnfoldedContactDetailsRef.current = false;
						return submitted;
					},
					reset: () => {
						hasUnfoldedContactDetailsRef.current = false;
						contact.reset();
					},
					wantsRoom: true,
				}
			: undefined;
	const resolvedAdditionalAddTab = additionalAddTab ?? contactAddTab;
	const additionalAddTabId = resolvedAdditionalAddTab?.tab;
	const enabledComposerTabs = useMemo(
		() => [
			...COMPOSER_TABS.filter((tab) =>
				tab === ComposerTab.Record ? canCreateRecords : tab === ComposerTab.Task ? canCreateTasks : false,
			),
			...(additionalAddTabId ? [additionalAddTabId] : []),
		],
		[additionalAddTabId, canCreateRecords, canCreateTasks],
	);
	const canShowAddButton = showAddButton && enabledComposerTabs.length > 0;
	const resolvedInitialAddTab = enabledComposerTabs.includes(initialAddTab)
		? initialAddTab
		: (enabledComposerTabs[0] ?? initialAddTab);
	const { isAddOpen, activeTab, selectTab, openAdd, toggleAdd, closeAdd } =
		useAssistantComposer(resolvedInitialAddTab);
	const isInsideComposerDock = useComposerDockFocus(isAddOpen);
	const requestedAddTab = openAddTabRequest?.tab;
	const requestedAddTabKey = openAddTabRequest?.key;
	useEffect(() => {
		if (requestedAddTabKey === undefined || !requestedAddTab || !enabledComposerTabs.includes(requestedAddTab)) {
			return;
		}
		openAdd();
		selectTab(requestedAddTab);
	}, [enabledComposerTabs, openAdd, requestedAddTab, requestedAddTabKey, selectTab]);
	const record = useRecordComposer({ onSubmitted: closeAdd, defaultPrivate });
	const task = useTaskComposer({
		onSubmitted: (entry) => {
			closeAdd();
			onTaskSubmitted?.(entry);
		},
		childCreationEnabled: taskChildCreationEnabled,
		parentTask: taskParent,
	});
	const resetTask = task.reset;
	const openedTaskParentIdRef = useRef<number | null>(null);
	useEffect(() => {
		if (!taskParent) {
			openedTaskParentIdRef.current = null;
			return;
		}
		if (!canCreateTasks) return;
		if (openedTaskParentIdRef.current === taskParent.id) return;

		openedTaskParentIdRef.current = taskParent.id;
		resetTask();
		openAdd();
		selectTab(ComposerTab.Task);
	}, [canCreateTasks, openAdd, resetTask, selectTab, taskParent]);
	const setTaskStartDate = task.setStartDate;
	const skipAddCloseTransition = useCloseAddOnRouteChange({ isAddOpen, closeAdd });
	useEffect(() => {
		if (isAddOpen && !canShowAddButton) closeAdd();
	}, [canShowAddButton, closeAdd, isAddOpen]);

	// Dragging files anywhere reveals the drop overlay, which self-gates its private half.
	const isDraggingFiles = useWindowFileDrag();

	// System isn't wired yet, so its pill stays disabled until built.
	const isRecordTab = activeTab === ComposerTab.Record;
	const isTaskTab = activeTab === ComposerTab.Task;
	const isAdditionalTab = resolvedAdditionalAddTab?.tab === activeTab;
	// Surface the composer mode so the assistant heading can rotate its prompt with it.
	const headingMode = !isAddOpen ? 'ask' : isTaskTab ? 'task' : isAdditionalTab ? 'contact' : 'record';
	useEffect(() => {
		onHeadingModeChange?.(headingMode);
	}, [headingMode, onHeadingModeChange]);
	const addCanSubmit =
		(isRecordTab && record.canSubmit) ||
		(isTaskTab && task.canSubmit) ||
		(Boolean(isAdditionalTab) && Boolean(resolvedAdditionalAddTab?.canSubmit));
	const addIsSubmitting =
		(isRecordTab && record.isSubmitting) ||
		(isTaskTab && task.isSubmitting) ||
		(Boolean(isAdditionalTab) && Boolean(resolvedAdditionalAddTab?.isSubmitting));
	const hasPendingDraft = record.isDirty || task.isDirty || Boolean(resolvedAdditionalAddTab?.isDirty);
	const dirtyComposerTabs = [
		...(record.isDirty ? [ComposerTab.Record] : []),
		...(task.isDirty ? [ComposerTab.Task] : []),
		...(resolvedAdditionalAddTab?.isDirty ? [resolvedAdditionalAddTab.tab] : []),
	];
	// Hint surfaced on the disabled submit so the user knows what's still missing.
	const addDisabledHint = isAdditionalTab
		? (resolvedAdditionalAddTab?.disabledHint ?? 'Complete the form')
		: getAddDisabledHint(isRecordTab, task);
	const activeChildCount = task.children.length - task.removingChildIds.length;
	const submitLabel =
		isTaskTab && taskParent
			? 'Add sub-task'
			: isTaskTab && activeChildCount > 0
				? `Add ${activeChildCount + 1} tasks`
				: COMPOSER_SUBMIT_LABEL[activeTab];
	const submitShortLabel =
		isTaskTab && activeChildCount > 0 ? `Add ${activeChildCount + 1}` : COMPOSER_SUBMIT_SHORT_LABEL[activeTab];
	const clearActiveDraft = () => {
		if (isRecordTab) record.setValue('');
		else if (isTaskTab) task.reset();
		else if (isAdditionalTab) resolvedAdditionalAddTab?.reset();
	};
	const handleAddSubmit = async () => {
		if (isRecordTab && canCreateRecords) record.submit();
		else if (isTaskTab && canCreateTasks) task.submit();
		else if (isAdditionalTab && resolvedAdditionalAddTab) {
			const submitted = await resolvedAdditionalAddTab.onSubmit();
			if (submitted !== false) closeAdd();
		}
	};

	const isBusy = isLoadingData || isRunning;
	const canSubmitQuery = !!query.trim() && !isBusy;

	// Mobile gets a bottom sheet for the add composer (instead of inline) and never autofocuses
	// (would pop the keyboard on load).
	const isMobile = useIsMobileViewport();
	const viewRecords = useCallback(() => {
		void navigate(
			propertyNavigateOptions(house.public_id, PropertyRouteTo.records, {
				id: askReadinessContext?.filePublicId,
			}),
		);
	}, [askReadinessContext?.filePublicId, house.public_id, navigate]);
	const viewRecordsLabel = askReadinessContext?.filePublicId ? 'View file' : 'View records';
	const shouldAutoFocus = autoFocus && !isMobile;
	const resolvedPlaceholder = isMobile && mobilePlaceholder ? mobilePlaceholder : placeholder;

	// Live list-search for hosts that wire it (records page): forward the text while it reads
	// as a search term, clear it once it reads as a question — same gating the old
	// SearchOrAskBar used so the list doesn't filter on a half-typed prompt.
	useEffect(() => {
		if (!onFilterChange) return;
		onFilterChange(isAsking(query) ? '' : query);
	}, [query, onFilterChange]);

	const submitTooltip = isRunning
		? 'Processing your query...'
		: isLoadingData
			? 'Loading...'
			: askReadinessState
				? getAskReadinessTooltip(askReadinessState, askReadinessContext?.affectedFileCount)
				: canSubmitQuery
					? askLabel
					: 'Type a question to ask Bundle';

	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [isMultiLine, setIsMultiLine] = useState(false);
	const [isComposerOverlayOpen, setIsComposerOverlayOpen] = useState(false);
	const [isComposerExpanded, setIsComposerExpanded] = useState(false);
	const {
		composerFrameRef,
		bottomRowRef,
		belowControlsRef,
		belowControlsScrollerRef,
		addHeaderRef,
		addFormRef,
		maxBelowControlsHeight,
		visibleAddHeight,
		maxAddFormHeight,
		isAddBodyScrollable,
	} = useComposerAddRegionLayout({
		isOpen: isAddOpen,
		isMobile,
		expandsDown,
		activeTab,
		isExpanded: isComposerExpanded,
		primaryTaskIsRecurring: task.isRecurring,
		belowControlsVersion: task.children
			.map(
				(child) =>
					`${child.id}:${child.isRecurring ? 'repeat' : 'once'}:${
						task.removingChildIds.includes(child.id) ? 'removing' : 'active'
					}`,
			)
			.join('|'),
	});

	// Grow the ask field to its content and track whether it spans multiple lines. The textarea is
	// only mounted while the inline add region is closed, so a desktop add expansion auto-skips the
	// resize (the ref is null) without an explicit guard.
	useAutoResizeTextarea(textareaRef, query, {
		maxHeight: COMPOSER_TEXTAREA_MAX_H,
		onResize: (scrollHeight) => setIsMultiLine(scrollHeight > COMPOSER_MULTILINE_THRESHOLD_PX),
	});

	useEffect(() => {
		if (!isAddOpen) {
			setIsComposerOverlayOpen(false);
			setIsComposerExpanded(false);
			// A picked date is stale after dismissal; reopening starts from today's default.
			setTaskStartDate(formatLocalDateForApi());
		}
	}, [isAddOpen, setTaskStartDate]);

	// Return focus to the ask input when the add composer closes (desktop only —
	// on mobile add is a sheet and refocusing would re-pop the keyboard). Keyed off
	// the open→closed transition so it doesn't grab focus on mount.
	const prevAddOpenRef = useRef(isAddOpen);
	useEffect(() => {
		if (prevAddOpenRef.current && !isAddOpen && !isMobile) {
			textareaRef.current?.focus();
		}
		prevAddOpenRef.current = isAddOpen;
	}, [isAddOpen, isMobile]);

	const prevRunningRef = useRef(false);
	useEffect(() => {
		if (!prevRunningRef.current && isRunning && !isMobile && !isAddOpen) {
			const frameId = requestAnimationFrame(() => textareaRef.current?.focus());
			prevRunningRef.current = isRunning;
			return () => cancelAnimationFrame(frameId);
		}
		prevRunningRef.current = isRunning;
	}, [isRunning, isMobile, isAddOpen]);

	const closeDesktopAddOnOutsideClick = useCallback(() => {
		if (isAddOpen && !isMobile && !isComposerOverlayOpen) closeAdd();
	}, [closeAdd, isAddOpen, isComposerOverlayOpen, isMobile]);
	useClickOutside(composerFrameRef, closeDesktopAddOnOutsideClick, {
		shouldIgnore: (event) =>
			event
				.composedPath()
				.some(
					(target) => target instanceof Element && !!target.closest(COMPOSER_PORTAL_CLICK_BOUNDARY_SELECTOR),
				),
	});

	// The bar only morphs/expands inline on desktop; on mobile add lives in a sheet.
	const isBarExpanded = isMultiLine || (!isMobile && isAddOpen);
	const readinessNeedsAskIntent = waitForAskIntent || !!onFilterChange;
	const [dismissedAskReadinessState, setDismissedAskReadinessState] =
		useState<KnowledgeBaseReadinessStateValue | null>(null);
	const isAskReadinessDismissed = askReadinessDismissed ?? dismissedAskReadinessState === askReadinessState;
	const showAskReadinessAdvisory =
		!isAddOpen &&
		askReadinessState !== null &&
		!isAskReadinessDismissed &&
		!!query.trim() &&
		(!readinessNeedsAskIntent || isAsking(query));
	const [renderAskReadinessAdvisory, setRenderAskReadinessAdvisory] = useState(showAskReadinessAdvisory);
	const [isAskReadinessAdvisoryVisible, setIsAskReadinessAdvisoryVisible] = useState(showAskReadinessAdvisory);
	const [renderedAskReadinessState, setRenderedAskReadinessState] = useState(askReadinessState);
	useEffect(() => {
		if (askReadinessState === null) setDismissedAskReadinessState(null);
	}, [askReadinessState]);
	useEffect(() => {
		let removalTimer: ReturnType<typeof setTimeout> | undefined;
		let revealFrame: number | undefined;

		if (showAskReadinessAdvisory && askReadinessState) {
			setRenderedAskReadinessState(askReadinessState);
			setRenderAskReadinessAdvisory(true);
			revealFrame = requestAnimationFrame(() => setIsAskReadinessAdvisoryVisible(true));
		} else {
			setIsAskReadinessAdvisoryVisible(false);
			removalTimer = setTimeout(() => setRenderAskReadinessAdvisory(false), COMPOSER_ADVISORY_EXIT_MS);
		}

		return () => {
			if (revealFrame !== undefined) cancelAnimationFrame(revealFrame);
			if (removalTimer !== undefined) clearTimeout(removalTimer);
		};
	}, [askReadinessState, showAskReadinessAdvisory]);
	const showInlineAdd = isAddOpen && !isMobile;
	const composerBg = showInlineAdd && isRecordTab && record.isPrivate ? 'surface.subtle' : 'surface.default';
	const composerBorderColor = showInlineAdd ? 'secondary.fixed' : 'border.default';
	const composerActiveBorderColor = showInlineAdd ? 'secondary.fixed' : 'border.strong';

	// The add region grows by height and fades by opacity together. The fade finishes ~60ms
	// before the height settles, so the content is fully opaque just as the grow ends — no
	// clipped, half-faded edge. (The "opacity + height" pairing from the prototype.)
	const isRemovingTaskChild = task.removingChildIds.length > 0;
	const addRevealMs = !isAddOpen
		? DURATION_FORM_REVEAL_CLOSE_MS
		: isRemovingTaskChild
			? COMPOSER_CHILD_REMOVE_MS
			: DURATION_FORM_REVEAL_OPEN_MS;
	const addFadeMs = Math.max(addRevealMs - COMPOSER_ADD_FADE_LEAD_MS, 0);
	const isChildRegionOpen = task.children.some((child) => !task.removingChildIds.includes(child.id));
	const childRevealMs = isChildRegionOpen
		? DURATION_FORM_REVEAL_OPEN_MS
		: isRemovingTaskChild
			? COMPOSER_CHILD_REMOVE_MS
			: DURATION_FORM_REVEAL_CLOSE_MS;
	const childFadeMs = Math.max(childRevealMs - COMPOSER_ADD_FADE_LEAD_MS, 0);

	// Add-control entrances (only mount when add opens, so @starting-style fires). Attach ends far
	// left, so it sweeps in from the right; privacy sits right of it and moves right into place from
	// the left (a short nudge). The submit doesn't slide — it morphs in place (ComposerSubmitButton).
	const slideAttach = getSlideInCss('right', COMPOSER_ACTION_ROW_SLIDE_MS, COMPOSER_ATTACH_SLIDE_PX);
	const slidePrivacy = getSlideInCss('left', COMPOSER_ACTION_ROW_SLIDE_MS, COMPOSER_PRIVACY_SLIDE_PX);

	// On desktop the + is absolutely positioned to the LEFT of the bar so the bar spans
	// the full container and lines up with the transcript by construction; the + just
	// overhangs into the margin (no fragile width maths). Mobile keeps the inline
	// +/bar row. The offset is the button width + its gap to the bar — tune for spacing.
	const addButtonOffset = COMPOSER_ADD_BUTTON_OVERHANG;
	const addButtonWrapperProps = isMobile
		? { flexShrink: 0 as const }
		: {
				flexShrink: 0 as const,
				position: 'absolute' as const,
				left: `-${addButtonOffset}px`,
				top: expandsDown ? 0 : undefined,
				bottom: expandsDown ? undefined : 0,
				zIndex: 1,
			};
	const readinessAdvisory =
		renderAskReadinessAdvisory && renderedAskReadinessState ? (
			<Box ml={isMobile && canShowAddButton ? `${COMPOSER_ADD_BUTTON_OVERHANG}px` : 0}>
				<AskReadinessAdvisory
					state={renderedAskReadinessState}
					fileCount={askReadinessContext?.affectedFileCount}
					placement={expandsDown ? 'below' : 'above'}
					visible={isAskReadinessAdvisoryVisible}
					onViewRecords={!isRecordsPage ? viewRecords : undefined}
					viewRecordsLabel={viewRecordsLabel}
					onDismiss={
						!isMobile
							? () =>
									onDismissAskReadiness
										? onDismissAskReadiness()
										: setDismissedAskReadinessState(renderedAskReadinessState)
							: undefined
					}
				/>
			</Box>
		) : null;

	return (
		<>
			{isAddOpen && !isMobile && !isInsideComposerDock ? <ComposerFocusBackdrop /> : null}
			<VStack
				ref={composerFrameRef}
				align="stretch"
				gap={0}
				w="100%"
				position="relative"
				zIndex={isAddOpen && !isInsideComposerDock ? ZIndex.Composer : undefined}
			>
				{!expandsDown && readinessAdvisory}
				<HStack
					align={expandsDown ? 'flex-start' : 'flex-end'}
					// Mobile gap matches the desktop +-to-bar gap (12px, baked into COMPOSER_ADD_BUTTON_OVERHANG)
					// so the spacing doesn't shrink when the layout flips from absolute to inline.
					gap={isMobile ? 3 : 0}
					w="100%"
					position="relative"
					zIndex={1}
				>
					{canShowAddButton && (
						<Box {...addButtonWrapperProps}>
							<ComposerAddButton
								isOpen={isAddOpen}
								hasPendingInput={hasPendingDraft}
								disabled={isLoadingData}
								onClick={toggleAdd}
								boxShadow={surfaceShadow}
								disableTransition={skipAddCloseTransition}
							/>
						</Box>
					)}

					<Box
						as="form"
						onSubmit={onSubmit}
						// Bound around the whole bar rather than on each field, so the chord works from the
						// title, the details and the controls alike. Gated on the add region being open: with
						// it closed this is the ask field, where Enter already sends and this would be a
						// second, quieter way to do the same thing.
						onKeyDown={composerSubmitKeyDown(isAddOpen && addCanSubmit, handleAddSubmit)}
						flex={1}
						minW={0}
						bg={composerBg}
						borderWidth="1px"
						// While add is open, the elevated border + focus shadow are the BASE state, not only
						// :focus-within — opening moves focus through the + button (outside this form) for a
						// frame, so a focus-within-only treatment flickers off and back during the transition.
						borderColor={composerBorderColor}
						borderRadius={isBarExpanded ? COMPOSER_EXPANDED_RADIUS : COMPOSER_COLLAPSED_RADIUS}
						boxShadow={showInlineAdd ? focusShadow : surfaceShadow}
						transition={skipAddCloseTransition ? 'none' : TRANSITION_COMPOSER_MORPH}
						// Bar stays overflow-visible so inner button shadows aren't clipped; the
						// add-region reveal does its own clipping for the height animation.
						overflow="visible"
						_hover={{
							'&:not(:focus-within)': {
								borderColor: composerActiveBorderColor,
							},
						}}
						_focusWithin={{
							borderColor: composerActiveBorderColor,
							boxShadow: focusShadow,
						}}
					>
						{/* Desktop inline "Add" region — height animates to its measured content so
					    open/close, tab switches and field growth ease rather than snap. Mobile
					    uses the bottom sheet instead. */}
						{!isMobile && (
							<Box
								overflow="hidden"
								minH={0}
								style={{
									height: isAddOpen ? visibleAddHeight : 0,
									opacity: isAddOpen ? 1 : 0,
								}}
								css={{
									transition: skipAddCloseTransition
										? 'none'
										: `height ${addRevealMs}ms ${EASE_OUT}, opacity ${addFadeMs}ms ${EASE_OUT}`,
									// Reduced motion: keep the fade for comprehension, drop the grow.
									'@media (prefers-reduced-motion: reduce)': { transition: 'opacity 150ms ease' },
								}}
							>
								<Box h="full" overflow="hidden">
									<Box ref={addHeaderRef}>
										<ComposerTabStrip
											activeTab={activeTab}
											onSelect={selectTab}
											tabs={enabledComposerTabs}
											dirtyTabs={dirtyComposerTabs}
											onOverlayOpenChange={setIsComposerOverlayOpen}
											isExpanded={isComposerExpanded}
											onToggleExpanded={() => {
												if (
													!isComposerExpanded &&
													isAdditionalTab &&
													!hasUnfoldedContactDetailsRef.current
												) {
													hasUnfoldedContactDetailsRef.current = true;
													setContactMoreDetailsRequest((key) => key + 1);
												}
												setIsComposerExpanded((expanded) => !expanded);
											}}
											onClear={clearActiveDraft}
										/>
									</Box>
									<Box
										maxH={maxAddFormHeight ?? undefined}
										overflowY={isAddBodyScrollable ? 'auto' : 'visible'}
									>
										<Box ref={addFormRef}>
											<ComposerForms
												activeTab={activeTab}
												record={record}
												task={task}
												isOpen={isAddOpen}
												showTaskChildren={false}
												onClearTaskParent={onClearTaskParent}
												additionalTab={
													resolvedAdditionalAddTab
														? {
																tab: resolvedAdditionalAddTab.tab,
																form: resolvedAdditionalAddTab.form,
															}
														: undefined
												}
											/>
										</Box>
									</Box>
								</Box>
							</Box>
						)}

						{/* Bottom row: the LEFT content swaps (ask field ↔ add controls), but the submit on
					    the right PERSISTS and morphs (round send icon ↔ labelled pill) so it reads as one
					    element reshaping. Add controls sweep/slide in; submit stays put and morphs. */}
						<HStack
							ref={bottomRowRef}
							gap={2}
							px={showInlineAdd ? 4 : 2}
							py={showInlineAdd ? 4 : 2}
							align="flex-end"
							w="100%"
							flexShrink={0}
							css={COMPOSER_ACTION_ROW_CONTAINER_CSS}
						>
							{/* Left column nests the controls row + the frequency row that expands below it, so the
						    submit button (outside this column) stays pinned to the bottom as the column grows. */}
							<VStack flex={1} minW={0} align="stretch" gap={0}>
								<HStack
									gap={2}
									w="100%"
									minW={0}
									align={isMultiLine && !showInlineAdd ? 'flex-end' : 'center'}
								>
									{showInlineAdd ? (
										<>
											{isRecordTab ? (
												<Box flex={1} minW={0} css={slideAttach}>
													<ComposerAttachActions
														onFilesSelected={record.submitFiles}
														disabled={record.isSubmitting}
													/>
												</Box>
											) : isTaskTab ? (
												<ComposerTaskScheduleControls
													task={task}
													title={task.title}
													controlsCss={slideAttach}
												/>
											) : (
												<Box flex={1} minW={0} />
											)}
											{isRecordTab && (
												<Box flexShrink={0} css={slidePrivacy}>
													<ComposerPrivacyToggle
														isPrivate={record.isPrivate}
														onToggle={record.togglePrivate}
														disabled={record.isSubmitting}
													/>
												</Box>
											)}
										</>
									) : (
										<>
											{leadingIcon && (
												<Box
													display="flex"
													alignItems="center"
													justifyContent="center"
													boxSize={5}
													ml={2}
													flexShrink={0}
													color="fg.muted"
													alignSelf={isMultiLine ? 'flex-start' : 'center'}
													mt={isMultiLine ? 2 : 0}
												>
													{leadingIcon}
												</Box>
											)}
											<Textarea
												ref={textareaRef}
												value={query}
												onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
													setQuery(e.target.value)
												}
												placeholder={resolvedPlaceholder}
												aria-label="Query input"
												autoFocus={shouldAutoFocus}
												rows={1}
												flex={1}
												minW={0}
												minH={0}
												py={2}
												pl={leadingIcon ? 1 : 3}
												pr={1}
												textStyle="body"
												fontFamily="body"
												lineHeight="shorter"
												verticalAlign="top"
												maxH={`${COMPOSER_TEXTAREA_MAX_H}px`}
												border={0}
												bg="transparent"
												resize="none"
												overflowY="auto"
												// Textarea placeholders soft-wrap by default; clamp to one line so a long
												// placeholder truncates (…) on narrow widths instead of wrapping to two rows.
												_placeholder={{
													color: 'fg.muted',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}
												_focus={{
													boxShadow: 'none',
													border: 0,
													outline: 'none',
													borderColor: 'transparent',
												}}
												_disabled={{ opacity: 0.6, cursor: 'default' }}
												onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
													if (e.key === 'Enter' && !e.shiftKey && canSubmitQuery) {
														e.preventDefault();
														onSubmit(e);
													}
												}}
											/>
										</>
									)}
								</HStack>
							</VStack>

							<Box alignSelf="flex-end" flexShrink={0}>
								<HStack gap={2}>
									{showInlineAdd && isTaskTab && task.childCreationEnabled && (
										<Button
											{...COMPOSER_ACTION_LABEL_PILL_PROPS}
											aria-label="Add sub-task"
											minW={0}
											css={COMPOSER_SUBTASK_PILL_CSS}
											onClick={task.addChild}
											disabled={task.isSubmitting}
										>
											<Icon as={LuListPlus} boxSize={4} />
											<Text as="span" css={COMPOSER_SUBTASK_LABEL_CSS}>
												Sub-task
											</Text>
										</Button>
									)}
									<ComposerSubmitButton
										isAdd={showInlineAdd}
										canSubmitQuery={canSubmitQuery}
										submitTooltip={submitTooltip}
										askReadinessState={askReadinessState}
										multiLine={isMultiLine}
										addLabel={submitLabel}
										addShortLabel={submitShortLabel}
										addCanSubmit={addCanSubmit}
										addIsSubmitting={addIsSubmitting}
										addDisabledHint={addDisabledHint}
										onAddSubmit={handleAddSubmit}
									/>
								</HStack>
							</Box>
						</HStack>
						{showInlineAdd && isTaskTab && task.childCreationEnabled && (
							<Box
								display="grid"
								flexShrink={0}
								style={{
									gridTemplateRows: isChildRegionOpen ? '1fr' : '0fr',
									opacity: isChildRegionOpen ? 1 : 0,
								}}
								css={{
									transition: skipAddCloseTransition
										? 'none'
										: `grid-template-rows ${childRevealMs}ms ${EASE_OUT}, opacity ${childFadeMs}ms ${EASE_OUT}`,
									'@media (prefers-reduced-motion: reduce)': {
										transition: 'opacity 150ms ease',
									},
								}}
							>
								<Box minH={0} overflow="hidden" borderBottomRadius={COMPOSER_EXPANDED_RADIUS}>
									<Box
										ref={belowControlsScrollerRef}
										maxH={maxBelowControlsHeight ?? undefined}
										overflowY="auto"
										css={{ ...dropdownScrollbarCss, scrollbarGutter: 'auto' }}
									>
										<Box
											ref={belowControlsRef}
											pl={4}
											pb={2}
											css={{
												paddingRight:
													'max(0px, calc(16px - var(--composer-scrollbar-width, 0px)))',
											}}
										>
											<ComposerChildTasks task={task} />
										</Box>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</HStack>
				{expandsDown && readinessAdvisory}
			</VStack>

			{showAddButton && canCreateRecords && isDraggingFiles && (
				<FileDropOverlay onDropFiles={(files, isPrivate) => record.submitFiles(files, isPrivate)} />
			)}

			{isMobile && (
				<ComposerAddSheet
					isOpen={isAddOpen}
					onClose={closeAdd}
					activeTab={activeTab}
					onSelectTab={selectTab}
					record={record}
					task={task}
					isRecordTab={isRecordTab}
					isTaskTab={isTaskTab}
					submitLabel={submitLabel}
					canSubmit={addCanSubmit}
					isSubmitting={addIsSubmitting}
					onSubmit={handleAddSubmit}
					tabs={enabledComposerTabs}
					dirtyTabs={dirtyComposerTabs}
					onClearDraft={clearActiveDraft}
					additionalTab={
						resolvedAdditionalAddTab
							? {
									tab: resolvedAdditionalAddTab.tab,
									form: resolvedAdditionalAddTab.form,
									wantsRoom: resolvedAdditionalAddTab.wantsRoom,
								}
							: undefined
					}
				/>
			)}
		</>
	);
};

export default AssistantInput;
